import { brl } from "@/lib/api";
import { Surface } from "@/components/ds";
import { TrendLine, C } from "./charts";

const DEV_LABELS = {
  EM_DESENVOLVIMENTO: "Em desenvolvimento",
  EM_TESTES: "Em testes",
  AJUSTES: "Precisou de ajustes",
  APROVADO: "Aprovados",
  REPROVADO: "Reprovados",
  ARQUIVADO: "Arquivados",
};

const Cell = ({ label, value, hint, tone, testid }) => (
  <div className="rounded-lg border border-white/10 bg-secondary/40 px-4 py-3" data-testid={testid}>
    <p className="text-[11px] uppercase tracking-widest text-muted-foreground">{label}</p>
    <p className={`mt-1 font-num text-lg font-semibold ${tone || ""}`}>{value}</p>
    {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
  </div>
);

export function FinanceProduction({ data }) {
  const p = data.production;
  const d = data.development;
  const unitCost = p.units ? p.cost / p.units : null;

  return (
    <div className="space-y-6" data-testid="finance-production">
      <Surface className="p-5">
        <h2 className="text-base font-medium md:text-lg">Produção no período</h2>
        <p className="mt-1 text-xs text-muted-foreground">
          Custo real gravado nas produções concluídas (material, insumos, máquina e energia).
          Produção concluída não gera receita — só o pagamento confirmado gera.
        </p>
        <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <Cell label="Produções concluídas" value={p.productions} hint={`${p.open} em aberto`} testid="prod-count" />
          <Cell label="Unidades boas" value={p.units} hint={`${p.lost} perdida(s)`} testid="prod-units" />
          <Cell
            label="Tempo de produção"
            value={`${Math.round(p.time_min / 60)} h`}
            hint={`${p.time_min} min`}
            testid="prod-time"
          />
          <Cell
            label="Filamento consumido"
            value={`${p.filament_g} g`}
            hint={`${(p.filament_g / 1000).toFixed(2)} kg`}
            testid="prod-filament"
          />
          <Cell
            label="Custo de material"
            value={brl(p.material_cost)}
            hint="filamento + insumos"
            testid="prod-material-cost"
          />
          <Cell
            label="Custo total de produção"
            value={p.cost ? brl(p.cost) : "não disponível"}
            hint={p.without_cost ? `${p.without_cost} produção(ões) sem custo` : "todas com custo"}
            tone="text-amber-300"
            testid="prod-total-cost"
          />
          <Cell
            label="Custo por unidade"
            value={unitCost ? brl(unitCost) : "não disponível"}
            testid="prod-unit-cost"
          />
          <Cell
            label="Vendido no período"
            value={brl(data.kpis.sold)}
            hint={
              data.kpis.operational_result === null
                ? "resultado operacional não disponível"
                : `resultado estimado ${brl(data.kpis.operational_result)}`
            }
            tone="text-emerald-300"
            testid="prod-vs-sales"
          />
        </div>
      </Surface>

      <Surface className="p-5">
        <h3 className="text-sm font-medium">Vendas × unidades produzidas</h3>
        <p className="mb-3 text-xs text-muted-foreground">
          Comparativo mensal — venda e produção podem cair em meses diferentes
        </p>
        <TrendLine
          data={data.series}
          lines={[
            { key: "sold", name: "Vendido (R$)", color: C.sold },
            { key: "production_cost", name: "Custo produção (R$)", color: C.cost },
          ]}
          testid="chart-production-trend"
        />
        <TrendLine
          data={data.series}
          lines={[{ key: "units", name: "Unidades produzidas", color: C.received }]}
          money={false}
          height={180}
          testid="chart-production-units"
        />
      </Surface>

      <Surface className="p-5" data-testid="development-block">
        <h2 className="text-base font-medium md:text-lg">Desenvolvimento / Laboratório</h2>
        <p className="mt-1 text-xs text-muted-foreground">
          Custo real das produções de teste concluídas no período. Nada é recalculado aqui.
        </p>
        <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <Cell label="Projetos" value={d.projects_total} hint={`${d.projects_in_period} criado(s) no período`} testid="dev-projects" />
          <Cell label="Testes no período" value={d.tests} testid="dev-tests" />
          <Cell
            label="Custo de desenvolvimento"
            value={d.cost ? brl(d.cost) : "não disponível"}
            tone="text-amber-300"
            testid="dev-cost"
          />
          <Cell
            label="Custo por projeto"
            value={d.cost_per_project ? brl(d.cost_per_project) : "não disponível"}
            hint={`${d.projects_with_cost} projeto(s) com custo no período`}
            testid="dev-cost-project"
          />
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {Object.entries(d.by_status).map(([status, count]) => (
            <span
              key={status}
              className="rounded-full border border-white/10 bg-secondary/40 px-3 py-1 text-[11px] text-muted-foreground"
              data-testid={`dev-status-${status}`}
            >
              {DEV_LABELS[status] || status}: <strong className="text-foreground">{count}</strong>
            </span>
          ))}
        </div>
      </Surface>
    </div>
  );
}
